import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { isCameraChangeEvent } from '../lib/mappedin-types';
import { useAppStore, type AppState } from '../state/app-store';

const GLOBE_SIZE = 1350;
const HALF = GLOBE_SIZE / 2;
const RADIUS = GLOBE_SIZE * 0.42;

const HEX_SIZE = 1;
const HEX_H = HEX_SIZE * Math.sqrt(3);
const HEX_W = HEX_SIZE * 1.5;

const EARTH_IMG_URL = 'https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-night.jpg';

interface Point2D {
  x: number;
  y: number;
  z: number;
}

function latLngTo2D(
  lat: number,
  lng: number,
  rotY: number,
  cx: number,
  cy: number,
  r: number,
): Point2D {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + rotY) * (Math.PI / 180);
  const x3 = r * Math.sin(phi) * Math.cos(theta);
  const y3 = r * Math.cos(phi);
  const z3 = r * Math.sin(phi) * Math.sin(theta);
  return { x: cx + x3, y: cy - y3, z: z3 };
}

function toHexCell(lat: number, lng: number): { row: number; col: number } {
  const row = Math.round(lat / HEX_H);
  const offset = row % 2 !== 0 ? HEX_W / 2 : 0;
  const col = Math.round((lng - offset) / HEX_W);
  return { row, col };
}

function hexCellCenter(row: number, col: number): { lat: number; lng: number } {
  const offset = row % 2 !== 0 ? HEX_W / 2 : 0;
  return { lat: row * HEX_H, lng: col * HEX_W + offset };
}

interface HexBinPoint {
  id: string;
  latitude: number;
  longitude: number;
  name?: string;
  city?: string;
  state?: string;
}

interface HexBin {
  lat: number;
  lng: number;
  row: number;
  col: number;
  key: string;
  points: HexBinPoint[];
}

interface HexBinData {
  bins: HexBin[];
  maxCount: number;
  byKey: Map<string, HexBin>;
}

interface GlobeState {
  rotation: number;
  autoRotateSpeed: number;
  isDragging: boolean;
  dragStartX: number;
  dragStartRot: number;
  lastInteraction: number;
  posT: number;
  targetPosT: number;
  targetRotation: number | null;
  beamScale: number;
  beamSpread: number;
  beamSpreadTarget: number;
  beamPhase: 'idle' | 'ray' | 'spreading' | 'done';
}

interface EarthPixels {
  data: Uint8ClampedArray;
  w: number;
  h: number;
}

interface Tooltip {
  x: number;
  y: number;
  names: string[];
  city: string;
  count: number;
}

function drawEarth(
  ctx: CanvasRenderingContext2D,
  rot: number,
  cx: number,
  cy: number,
  earthPx: EarthPixels,
): void {
  const { data: pixels, w: imgW, h: imgH } = earthPx;
  const outData = ctx.createImageData(GLOBE_SIZE, GLOBE_SIZE);
  const out = outData.data;
  const r2 = RADIUS * RADIUS;
  for (let py = Math.floor(cy - RADIUS); py < cy + RADIUS; py++) {
    const dy = py - cy;
    const hw = Math.sqrt(r2 - dy * dy);
    for (let px = Math.floor(cx - hw); px < cx + hw; px++) {
      const dx = px - cx;
      const z = Math.sqrt(Math.max(0, r2 - dx * dx - dy * dy));
      const lat = Math.asin(-dy / RADIUS) * (180 / Math.PI);
      const lng = Math.atan2(z, dx) * (180 / Math.PI) - rot;
      const tx = Math.floor((((((lng + 180) % 360) + 360) % 360) / 360) * imgW) % imgW;
      const ty = Math.floor(((90 - lat) / 180) * imgH) % imgH;
      const si = (ty * imgW + tx) * 4;
      const di = (py * GLOBE_SIZE + px) * 4;
      const ef = 0.3 + 0.7 * (z / RADIUS);
      out[di] = (pixels[si] ?? 0) * ef;
      out[di + 1] = (pixels[si + 1] ?? 0) * ef;
      out[di + 2] = (pixels[si + 2] ?? 0) * ef;
      out[di + 3] = 230;
    }
  }
  ctx.putImageData(outData, 0, 0);
}

function drawFallbackEarth(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  const grad = ctx.createRadialGradient(
    cx - RADIUS * 0.25,
    cy - RADIUS * 0.25,
    RADIUS * 0.05,
    cx,
    cy,
    RADIUS,
  );
  grad.addColorStop(0, '#1e2444');
  grad.addColorStop(0.5, '#141a30');
  grad.addColorStop(0.85, '#0e1320');
  grad.addColorStop(1, '#080b15');
  ctx.beginPath();
  ctx.arc(cx, cy, RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
}

function drawRim(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  const rimGrad = ctx.createRadialGradient(cx, cy, RADIUS * 0.94, cx, cy, RADIUS * 1.06);
  rimGrad.addColorStop(0, 'rgba(99,102,241,0)');
  rimGrad.addColorStop(0.5, 'rgba(99,102,241,0.045)');
  rimGrad.addColorStop(1, 'rgba(99,102,241,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, RADIUS * 1.04, 0, Math.PI * 2);
  ctx.fillStyle = rimGrad;
  ctx.fill();
}

function updatePhysics(
  s: GlobeState,
  store: AppState,
  hexBins: HexBinData,
  cx: number,
  cy: number,
): void {
  s.posT += (s.targetPosT - s.posT) * 0.04;
  if (store.showVenueMap && s.targetPosT === 1) {
    s.beamScale = Math.min(1, s.beamScale + 0.015);
  } else if (s.targetPosT === 0) {
    s.beamScale = Math.max(0, s.beamScale - 0.04);
  }
  const idleLong =
    s.targetRotation == null && s.autoRotateSpeed === 0 && Date.now() - s.lastInteraction > 3000;
  if (idleLong) s.autoRotateSpeed = 0.08;

  if (s.targetRotation != null) {
    s.rotation += (s.targetRotation - s.rotation) * 0.12;
    return;
  }
  if (s.autoRotateSpeed !== 0 && hexBins.bins.length > 0) {
    let facing = 0;
    for (const bin of hexBins.bins) {
      if (latLngTo2D(bin.lat, bin.lng, s.rotation, cx, cy, RADIUS).z > RADIUS * 0.3) {
        facing++;
      }
    }
    if (facing / hexBins.bins.length < 0.35) s.autoRotateSpeed = -s.autoRotateSpeed;
  }
  s.rotation += s.autoRotateSpeed;
}

function binMatchesSearch(bin: HexBin, q: string): boolean {
  return bin.points.some(
    (v) =>
      (v.name ?? '').toLowerCase().includes(q) ||
      (v.city ?? '').toLowerCase().includes(q) ||
      (v.state ?? '').toLowerCase().includes(q),
  );
}

function drawHoveredBin(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  baseR: number,
  df: number,
): void {
  ctx.beginPath();
  ctx.arc(x, y, baseR * 2.5, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(165,180,252,${(0.06 * df).toFixed(2)})`;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, baseR * 1.6, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(165,180,252,${(0.14 * df).toFixed(2)})`;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, baseR, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(199,210,254,${(0.7 * df).toFixed(2)})`;
  ctx.fill();
}

function drawNormalBin(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  baseR: number,
  df: number,
): void {
  ctx.beginPath();
  ctx.arc(x, y, baseR * 2.2, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(0,200,200,${(0.03 * df).toFixed(2)})`;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, baseR * 1.4, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(0,200,200,${(0.08 * df).toFixed(2)})`;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, baseR, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(0,210,210,${(0.55 * df).toFixed(2)})`;
  ctx.fill();
}

function drawBins(
  ctx: CanvasRenderingContext2D,
  rot: number,
  hexBins: HexBinData,
  store: AppState,
  selKey: string | null,
  cx: number,
  cy: number,
): void {
  const { bins, maxCount } = hexBins;
  const currentHover = store.hoveredBinKey;
  const searchQ = (store.globeSearchQuery || '').toLowerCase().trim();

  for (const bin of bins) {
    const p = latLngTo2D(bin.lat, bin.lng, rot, cx, cy, RADIUS);
    if (p.z <= 0) continue;
    if (searchQ && !binMatchesSearch(bin, searchQ)) continue;
    if (selKey === bin.key) continue;

    const t = Math.min(bin.points.length / Math.max(maxCount, 1), 1);
    const df = 0.5 + 0.5 * (p.z / RADIUS);
    const baseR = 3 + t * 4;

    if (currentHover === bin.key) {
      drawHoveredBin(ctx, p.x, p.y, baseR, df);
    } else {
      drawNormalBin(ctx, p.x, p.y, baseR, df);
    }
  }
}

function computeGroundOffsetSY(store: AppState, pitchRad: number, gap: number): number {
  const floors = store.floors;
  if (floors.length <= 1) return 0;
  const elevs = [...new Set(floors.map((f) => f.elevation ?? 0))].sort((a, b) => a - b);
  const eOrd: Record<number, number> = {};
  elevs.forEach((e, i) => {
    eOrd[e] = i;
  });
  const groundOrd = eOrd[0] ?? 0;
  const maxOrd = elevs.length - 1;
  const groundFrac = maxOrd > 0 ? (groundOrd - maxOrd / 2) / (maxOrd / 2) : 0;
  const totalStackAlt = maxOrd * gap;
  const screenOffset = groundFrac * (totalStackAlt / 2) * Math.cos(pitchRad);
  return screenOffset * 0.7;
}

function updateBeamPhase(s: GlobeState, store: AppState, maxSpread: number, bs: number): void {
  if (store.showVenueMap && s.beamPhase === 'idle') s.beamPhase = 'ray';
  if (s.beamPhase === 'ray' && bs >= 0.95) {
    s.beamPhase = 'spreading';
    s.beamSpreadTarget = maxSpread;
  }
  if (s.beamPhase === 'spreading' || s.beamPhase === 'done') {
    s.beamSpreadTarget = maxSpread;
  }
  if (s.targetPosT === 0) {
    s.beamPhase = 'idle';
    s.beamSpreadTarget = 0;
  }
  s.beamSpread += (s.beamSpreadTarget - s.beamSpread) * 0.06;
}

interface BeamGeometry {
  tipX: number;
  tipY: number;
  beamAngle: number;
  beamLen: number;
  maxSpread: number;
  alphaBoost: number;
}

function drawBeamRays(
  ctx: CanvasRenderingContext2D,
  s: GlobeState,
  geom: BeamGeometry,
  bs: number,
  cx: number,
  cy: number,
): void {
  if (bs <= 0) return;
  const offBeam = document.createElement('canvas');
  offBeam.width = GLOBE_SIZE;
  offBeam.height = GLOBE_SIZE;
  const bCtx = offBeam.getContext('2d');
  if (!bCtx) return;

  const layers = 10;
  const currentSpread = s.beamSpread;
  const { tipX, tipY, beamAngle, beamLen, alphaBoost } = geom;

  for (let i = layers; i >= 0; i--) {
    const frac = i / layers;
    const spread = currentSpread * frac;
    const alpha = 0.06 * (1 - frac * 0.4) * bs * alphaBoost;

    const lx = tipX + Math.cos(beamAngle - spread) * beamLen;
    const ly = tipY + Math.sin(beamAngle - spread) * beamLen;
    const rx = tipX + Math.cos(beamAngle + spread) * beamLen;
    const ry = tipY + Math.sin(beamAngle + spread) * beamLen;

    const gEndX = tipX + Math.cos(beamAngle) * beamLen;
    const gEndY = tipY + Math.sin(beamAngle) * beamLen;
    const grad = bCtx.createLinearGradient(tipX, tipY, gEndX, gEndY);
    grad.addColorStop(0, `rgba(0,200,255,${Math.min(alpha, 1)})`);
    grad.addColorStop(0.15, `rgba(0,200,255,${Math.min(alpha * 0.7, 1)})`);
    grad.addColorStop(0.35, `rgba(0,190,255,${Math.min(alpha * 0.25, 1)})`);
    grad.addColorStop(0.6, `rgba(0,180,255,${Math.min(alpha * 0.06, 1)})`);
    grad.addColorStop(1, 'rgba(0,180,255,0)');

    bCtx.beginPath();
    bCtx.moveTo(tipX, tipY);
    bCtx.lineTo(lx, ly);
    bCtx.lineTo(rx, ry);
    bCtx.closePath();
    bCtx.fillStyle = grad;
    bCtx.fill();
  }

  bCtx.globalCompositeOperation = 'destination-in';
  const fadeGrad = bCtx.createRadialGradient(cx, cy, 0, cx, cy, RADIUS * 1.05);
  fadeGrad.addColorStop(0, 'rgba(255,255,255,1)');
  fadeGrad.addColorStop(0.75, 'rgba(255,255,255,1)');
  fadeGrad.addColorStop(0.88, 'rgba(255,255,255,0.5)');
  fadeGrad.addColorStop(0.96, 'rgba(255,255,255,0.1)');
  fadeGrad.addColorStop(1, 'rgba(255,255,255,0)');
  bCtx.fillStyle = fadeGrad;
  bCtx.fillRect(0, 0, GLOBE_SIZE, GLOBE_SIZE);

  ctx.drawImage(offBeam, 0, 0);
}

function drawBeamTip(ctx: CanvasRenderingContext2D, tipX: number, tipY: number): void {
  ctx.beginPath();
  ctx.arc(tipX, tipY, 14, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,200,255,0.15)';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(tipX, tipY, 8, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(150,230,255,0.35)';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(tipX, tipY, 4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fill();
}

function drawVenueBeam(
  ctx: CanvasRenderingContext2D,
  s: GlobeState,
  store: AppState,
  rot: number,
  cx: number,
  cy: number,
): void {
  const sv = store.selectedVenue;
  if (!sv || !sv.latitude || !sv.longitude) return;
  const sp = latLngTo2D(sv.latitude, sv.longitude, rot, cx, cy, RADIUS);
  if (sp.z <= 0) return;

  const tipX = sp.x;
  const tipY = sp.y;
  const bs = s.beamScale;

  const GLOBE_CORNER_WIDTH = Math.ceil(1350 * 0.42 * 0.9) + 20;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const mapCenterSX = GLOBE_CORNER_WIDTH + (vw - GLOBE_CORNER_WIDTH - 40) / 2;
  const mapCenterSY = 40 + (vh - 40 - 40) / 2;

  const gap = store.floorGap || 40;
  const pitchRad = ((store.pitch || 60) * Math.PI) / 180;
  const groundOffsetSY = computeGroundOffsetSY(store, pitchRad, gap);

  const stackedT = Math.max(0, Math.min(1, s.beamSpread / Math.max(Math.PI / 30, 0.01)));
  const beamTargetSY = mapCenterSY + groundOffsetSY * (1 - stackedT);

  const targetCX = mapCenterSX / 0.9 + HALF;
  const targetCY = (beamTargetSY - vh - HALF * 0.25) / 0.9 + HALF;
  const beamAngle = Math.atan2(targetCY - tipY, targetCX - tipX);
  const beamLen = 900;

  const floorCount = Math.max(1, store.floors.length);
  const floorGap = store.floorGap || 40;
  const isStacked = store.isStacked;
  const FACADE_GAP = 4;
  const contractedHeight = (floorCount - 1) * FACADE_GAP;
  const stackedHeight = (floorCount - 1) * floorGap;
  const activeHeight = isStacked ? stackedHeight : contractedHeight;

  const MIN_SPREAD = Math.PI / 80;
  const maxSpread = Math.max(
    MIN_SPREAD,
    Math.min(Math.PI / 30 + activeHeight * 0.002, Math.PI / 4),
  );

  updateBeamPhase(s, store, maxSpread, bs);

  const singleFloor = floorCount <= 1;
  const alphaBoost = singleFloor ? 2.0 : isStacked ? 1.4 : 1.8;

  drawBeamRays(ctx, s, { tipX, tipY, beamAngle, beamLen, maxSpread, alphaBoost }, bs, cx, cy);
  drawBeamTip(ctx, tipX, tipY);
}

export function GlobeCorner(): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const earthPixelsRef = useRef<EarthPixels | null>(null);
  const venues = useAppStore((s) => s.venues);
  const selectedVenue = useAppStore((s) => s.selectedVenue);
  const appMode = useAppStore((s) => s.appMode);
  const hoveredBinKey = useAppStore((s) => s.hoveredBinKey);
  const mapViewForDep = useAppStore((s) => s.mapView);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = (): void => {
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);
      earthPixelsRef.current = {
        data: ctx.getImageData(0, 0, img.width, img.height).data,
        w: img.width,
        h: img.height,
      };
    };
    img.src = EARTH_IMG_URL;
  }, []);

  const initialRotation = useMemo(() => {
    const valid = venues.filter((v) => v.latitude && v.longitude);
    if (valid.length === 0) return -80;
    let sumLng = 0;
    for (const v of valid) sumLng += v.longitude;
    return 90 - sumLng / valid.length;
  }, [venues]);

  const stateRef = useRef<GlobeState>({
    rotation: -80,
    autoRotateSpeed: 0.08,
    isDragging: false,
    dragStartX: 0,
    dragStartRot: 0,
    lastInteraction: 0,
    posT: 0,
    targetPosT: 0,
    targetRotation: null,
    beamScale: 0,
    beamSpread: 0,
    beamSpreadTarget: 0,
    beamPhase: 'idle',
  });

  const venueRotation = useMemo(() => {
    if (!selectedVenue || selectedVenue.latitude == null) return initialRotation;
    const phi = (90 - selectedVenue.latitude) * (Math.PI / 180);
    const sinPhi = Math.sin(phi);
    const cosTheta = Math.min(1, Math.max(-1, HALF / (2 * RADIUS * sinPhi)));
    return Math.acos(cosTheta) * (180 / Math.PI) - selectedVenue.longitude + 30;
  }, [selectedVenue, initialRotation]);

  const hexBins = useMemo(() => {
    const valid = venues.filter((v) => v.latitude && v.longitude);
    const bins = new Map<string, HexBin>();
    let maxCount = 1;
    for (const v of valid) {
      const c = toHexCell(v.latitude, v.longitude);
      const key = `${c.row},${c.col}`;
      let bin = bins.get(key);
      if (!bin) {
        const center = hexCellCenter(c.row, c.col);
        bin = {
          lat: center.lat,
          lng: center.lng,
          row: c.row,
          col: c.col,
          key,
          points: [],
        };
        bins.set(key, bin);
      }
      const point: HexBinPoint = {
        id: v.id,
        name: v.name,
        latitude: v.latitude,
        longitude: v.longitude,
      };
      if (v.city) point.city = v.city;
      if (v.state) point.state = v.state;
      bin.points.push(point);
    }
    for (const b of bins.values()) {
      if (b.points.length > maxCount) maxCount = b.points.length;
    }
    return { bins: Array.from(bins.values()), maxCount, byKey: bins } satisfies HexBinData;
  }, [venues]);

  useEffect(() => {
    if (stateRef.current.targetRotation == null) stateRef.current.rotation = initialRotation;
  }, [initialRotation]);

  useEffect(() => {
    const s = stateRef.current;
    if (appMode === 'venue') {
      s.targetPosT = 1;
      s.targetRotation = venueRotation;
      s.autoRotateSpeed = 0;
    } else {
      s.targetPosT = 0;
      s.targetRotation = null;
      s.autoRotateSpeed = 0.08;
    }
  }, [appMode, venueRotation]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    function getMousePos(e: MouseEvent): { x: number; y: number } {
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) * GLOBE_SIZE) / rect.width,
        y: ((e.clientY - rect.top) * GLOBE_SIZE) / rect.height,
      };
    }

    function onMouseDown(e: MouseEvent): void {
      if (useAppStore.getState().appMode !== 'globe') return;
      const s = stateRef.current;
      s.isDragging = true;
      s.dragStartX = e.clientX;
      s.dragStartRot = s.rotation;
      s.autoRotateSpeed = 0;
      s.lastInteraction = Date.now();
    }

    function onMouseMove(e: MouseEvent): void {
      const s = stateRef.current;
      if (s.isDragging) {
        s.rotation = s.dragStartRot - (e.clientX - s.dragStartX) * 0.3;
        return;
      }
      if (useAppStore.getState().appMode !== 'globe') return;
      s.autoRotateSpeed = 0;
      s.lastInteraction = Date.now();
      const pos = getMousePos(e);
      let bestDist = 25;
      let bestKey: string | null = null;
      for (const bin of hexBins.bins) {
        const p = latLngTo2D(bin.lat, bin.lng, s.rotation, HALF, HALF, RADIUS);
        if (p.z <= 0) continue;
        const d = Math.hypot(pos.x - p.x, pos.y - p.y);
        if (d < bestDist) {
          bestDist = d;
          bestKey = bin.key;
        }
      }
      const cur = useAppStore.getState().hoveredBinKey;
      if (bestKey !== cur) useAppStore.getState().setHoveredBinKey(bestKey);
    }

    function onMouseUp(e: MouseEvent): void {
      const s = stateRef.current;
      if (!s.isDragging) return;
      const dx = Math.abs(e.clientX - s.dragStartX);
      s.isDragging = false;
      s.lastInteraction = Date.now();
      if (dx < 5 && useAppStore.getState().appMode === 'globe') {
        const pos = getMousePos(e);
        let bestDist = 30;
        let bestBin: HexBin | null = null;
        for (const bin of hexBins.bins) {
          const p = latLngTo2D(bin.lat, bin.lng, s.rotation, HALF, HALF, RADIUS);
          if (p.z <= 0) continue;
          const d = Math.hypot(pos.x - p.x, pos.y - p.y);
          if (d < bestDist) {
            bestDist = d;
            bestBin = bin;
          }
        }
        const first = bestBin?.points[0];
        if (first) {
          const full = useAppStore.getState().venues.find((v) => v.id === first.id);
          if (full) useAppStore.getState().pickVenue(full);
        }
      }
    }

    function onMouseLeave(): void {
      stateRef.current.isDragging = false;
      stateRef.current.lastInteraction = Date.now();
      useAppStore.getState().setHoveredBinKey(null);
    }

    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseLeave);
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [hexBins]);

  useEffect(() => {
    let animId = 0;
    const s = stateRef.current;

    function frame(): void {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) {
        animId = requestAnimationFrame(frame);
        return;
      }
      const cx = HALF;
      const cy = HALF;
      const store = useAppStore.getState();

      updatePhysics(s, store, hexBins, cx, cy);

      const rot = s.rotation;
      ctx.clearRect(0, 0, GLOBE_SIZE, GLOBE_SIZE);

      const earthPx = earthPixelsRef.current;
      if (earthPx) drawEarth(ctx, rot, cx, cy, earthPx);
      else drawFallbackEarth(ctx, cx, cy);

      drawRim(ctx, cx, cy);

      const selV = selectedVenue;
      const selKey =
        selV && selV.latitude && selV.longitude
          ? (() => {
              const c = toHexCell(selV.latitude, selV.longitude);
              return `${c.row},${c.col}`;
            })()
          : null;

      drawBins(ctx, rot, hexBins, store, selKey, cx, cy);
      drawVenueBeam(ctx, s, store, rot, cx, cy);

      animId = requestAnimationFrame(frame);
    }
    animId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(animId);
  }, [hexBins, selectedVenue]);

  const isCorner = appMode === 'venue';

  const [globeOpacity, setGlobeOpacity] = useState(1);
  const baseZoomRef = useRef<number | null>(null);
  useEffect(() => {
    const mapView = useAppStore.getState().mapView;
    if (!mapView || !isCorner) {
      setGlobeOpacity(1);
      baseZoomRef.current = null;
      return undefined;
    }
    let settled = false;
    const settleTimer = setTimeout(() => {
      settled = true;
      baseZoomRef.current = null;
    }, 4000);
    function onCam(event: unknown): void {
      if (!isCameraChangeEvent(event)) return;
      if (typeof event.zoomLevel !== 'number') return;
      if (!settled) return;
      if (baseZoomRef.current === null) baseZoomRef.current = event.zoomLevel;
      const delta = event.zoomLevel - baseZoomRef.current;
      const op = Math.min(1.0, 1 - Math.max(0, Math.min(1, delta / 2)));
      setGlobeOpacity(op);
    }
    mapView.on('camera-change', onCam);
    return () => {
      clearTimeout(settleTimer);
      try {
        mapView.off('camera-change', onCam);
      } catch {
        // Already detached.
      }
    };
  }, [isCorner, mapViewForDep]);

  const [viewport, setViewport] = useState<{ w: number; h: number }>(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : GLOBE_SIZE,
    h: typeof window !== 'undefined' ? window.innerHeight : GLOBE_SIZE,
  }));
  useEffect(() => {
    function onResize(): void {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  useEffect(() => {
    if (!hoveredBinKey || appMode !== 'globe') {
      setTooltip(null);
      return;
    }
    const bin = hexBins.byKey.get(hoveredBinKey);
    if (!bin) {
      setTooltip(null);
      return;
    }
    const s = stateRef.current;
    const p = latLngTo2D(bin.lat, bin.lng, s.rotation, HALF, HALF, RADIUS);
    if (p.z <= 0) {
      setTooltip(null);
      return;
    }
    const names = bin.points.map((v) => v.name ?? 'Unnamed');
    const first = bin.points[0];
    const city = first ? [first.city, first.state].filter(Boolean).join(', ') : '';
    const canvas = canvasRef.current;
    if (!canvas) {
      setTooltip(null);
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width / GLOBE_SIZE;
    const scaleY = rect.height / GLOBE_SIZE;
    const sx = rect.left + p.x * scaleX;
    const sy = rect.top + p.y * scaleY;
    setTooltip({ x: sx, y: sy, names, city, count: bin.points.length });
  }, [hoveredBinKey, appMode, hexBins]);

  // Fit the globe canvas inside the iframe: scale down when viewport is
  // smaller than the 1350×1350 source, so the globe never spills off
  // screen. Corner mode keeps its compact 0.9 scale.
  const fitScale = Math.min(0.8, (viewport.w - 40) / GLOBE_SIZE, (viewport.h - 40) / GLOBE_SIZE);
  const centerScale = Math.max(0.2, fitScale);

  return (
    <Fragment>
      <canvas
        ref={canvasRef}
        width={GLOBE_SIZE}
        height={GLOBE_SIZE}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: `${GLOBE_SIZE}px`,
          height: `${GLOBE_SIZE}px`,
          opacity: isCorner ? globeOpacity : 1,
          transform: isCorner
            ? `translate(-${HALF}px, calc(100vh - ${HALF * 0.75}px)) scale(0.9)`
            : `translate(calc(50vw - ${HALF}px), calc(50vh - ${HALF}px)) scale(${centerScale})`,
          transition: 'transform 1.2s cubic-bezier(0.4,0,0.2,1)',
          willChange: 'transform, opacity',
          zIndex: isCorner ? 10 : 9999,
          pointerEvents: appMode === 'globe' ? 'auto' : 'none',
          cursor: appMode === 'globe' ? (hoveredBinKey ? 'pointer' : 'grab') : 'default',
        }}
      />
      {tooltip && appMode === 'globe' && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%) translateY(-14px)',
            zIndex: 10001,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              background: 'rgba(15,20,35,0.92)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(99,102,241,0.25)',
              borderRadius: 10,
              padding: '8px 12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(99,102,241,0.15)',
              minWidth: 100,
              maxWidth: 220,
            }}
          >
            {tooltip.names.slice(0, 4).map((name, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key -- tooltip list is ephemeral
                key={i}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#e2e8f0',
                  lineHeight: '16px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {name}
              </div>
            ))}
            {tooltip.names.length > 4 && (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                +{tooltip.names.length - 4} more
              </div>
            )}
            {tooltip.city && (
              <div
                style={{
                  fontSize: 10,
                  color: '#6366f1',
                  marginTop: 3,
                  fontWeight: 500,
                }}
              >
                {tooltip.city}
              </div>
            )}
          </div>
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgba(15,20,35,0.92)',
              margin: '0 auto',
            }}
          />
        </div>
      )}
    </Fragment>
  );
}
